// Per-tank unit display + parse helpers. Storage is canonical:
// volume in US gallons, temperature readings in °F. The keeper's
// preference (set on the onboarding size step or in Tank settings)
// rides on tank.display_units = { volume: 'gal'|'L', temp: 'F'|'C' }.
// Anything outside the storage layer (Home cards, Sensei score,
// reading entry, livestock detail, Hydros tile) reads through these
// helpers so the same number can render as "20 gal" or "76 L" or
// "78°F" / "25.6°C" without the DB or AI prompts ever seeing the L/C.
//
// Conversions are exact: 1 US gal = 3.785411784 L. °C = (°F − 32) × 5/9.
// Round-trip safe to 4 decimals.

const L_PER_GAL = 3.785411784;

export function getDisplayUnits(tank) {
  const u = (tank && tank.display_units) || {};
  return {
    volume: u.volume === 'L' ? 'L' : 'gal',
    temp: u.temp === 'C' ? 'C' : 'F',
  };
}

// The units a new tank starts on: the ones most of the keeper's own tanks
// use. A litre / °C keeper adding a second tank was offered gal / °F.
export function defaultUnitsForNewTank(tanks) {
  const own = (tanks || []).filter((t) => t && (t.is_owner || t.role === 'owner'));
  const count = (field, metric) => own.filter((t) => getDisplayUnits(t)[field] === metric).length;
  const litres = count('volume', 'L');
  const celsius = count('temp', 'C');
  return {
    volume: litres > own.length - litres ? 'L' : 'gal',
    temp: celsius > own.length - celsius ? 'C' : 'F',
  };
}

// ── Volume ──────────────────────────────────────────────
export function galToL(gallons) {
  if (!Number.isFinite(Number(gallons))) return null;
  return Number(gallons) * L_PER_GAL;
}
export function lToGal(litres) {
  if (!Number.isFinite(Number(litres))) return null;
  return Number(litres) / L_PER_GAL;
}
export function formatVolume(gallons, unit, { decimals } = {}) {
  if (!Number.isFinite(Number(gallons))) return '—';
  const v = unit === 'L' ? galToL(gallons) : Number(gallons);
  const d = decimals != null ? decimals : v >= 100 ? 0 : v >= 10 ? 0 : 1;
  return `${v.toFixed(d)} ${unit === 'L' ? 'L' : 'gal'}`;
}
// Parses what the user typed into a canonical gallons number. Empty /
// invalid strings round-trip to null so the caller can decide whether
// that's "use the placeholder" or "block the form."
export function parseVolumeToGallons(text, unit) {
  if (text == null) return null;
  const n = Number(String(text).trim().replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return unit === 'L' ? lToGal(n) : n;
}

// ── Temperature ─────────────────────────────────────────
export function fToC(degF) {
  if (!Number.isFinite(Number(degF))) return null;
  return ((Number(degF) - 32) * 5) / 9;
}
export function cToF(degC) {
  if (!Number.isFinite(Number(degC))) return null;
  return (Number(degC) * 9) / 5 + 32;
}
export function formatTemp(degF, unit, { decimals = 1, withUnit = true } = {}) {
  if (!Number.isFinite(Number(degF))) return '—';
  const v = unit === 'C' ? fToC(degF) : Number(degF);
  const num = v.toFixed(decimals);
  return withUnit ? `${num}°${unit === 'C' ? 'C' : 'F'}` : num;
}
// User types a temperature — convert to °F for storage. Caller should
// pass the user's display unit so a "26" typed in Celsius doesn't get
// written as 26 °F.
export function parseTempToFahrenheit(text, unit) {
  if (text == null) return null;
  const s = String(text).trim().replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return unit === 'C' ? cToF(n) : n;
}

export function tempLabel(unit) {
  return unit === 'C' ? '°C' : '°F';
}
export function volumeLabel(unit) {
  return unit === 'L' ? 'L' : 'gal';
}

// A tank subtitle written before 2026-10-01 stored the raw gallons
// ("52.83441047162968 gal · sps-dominant") even for a litres keeper. The
// volume part is re-rendered in the tank's own unit; any other subtitle
// (the keeper's own words) is shown as typed.
const STORED_VOLUME_SUBTITLE = /^(\d+(?:\.\d+)?) gal\b(.*)$/;
export function displayTankSubtitle(tank) {
  const s = tank?.subtitle;
  if (typeof s !== 'string') return s ?? null;
  const m = s.match(STORED_VOLUME_SUBTITLE);
  if (!m) return s;
  return `${formatVolume(Number(m[1]), getDisplayUnits(tank).volume)}${m[2]}`;
}

// The onboarding size buckets are gallon ranges ("20 to 40"); a keeper who
// picked litres on the same screen read them as litres. On litres each
// label is rebuilt from its key in whole litres; a key that is not a range
// keeps its label.
export function sizeBucketsInUnit(options, unit) {
  if (unit !== 'L' || !Array.isArray(options)) return options || [];
  const l = (gal) => Math.round(galToL(gal));
  return options.map((o) => {
    const k = String(o?.k ?? '');
    let m;
    let label = null;
    if ((m = k.match(/^<(\d+(?:\.\d+)?)$/))) label = `Under ${l(m[1])} L`;
    else if ((m = k.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/)))
      label = `${l(m[1])} to ${l(m[2])} L`;
    else if ((m = k.match(/^(\d+(?:\.\d+)?)\+$/))) label = `Over ${l(m[1])} L`;
    return label ? { ...o, label, short_label: label } : o;
  });
}
